import React, { useState } from "react";
import { C, inp, lbl } from "../../../../lib/styles.js";
import { fl } from "../../../../lib/utils.js";
import { EQUIP_CHIP_COLORS, EQUIP_CHIP_DEFAULT } from "../../../../lib/constants.js";
import { subjectDisplay } from "./CaptureLog.jsx";

function getEquipColor(name) {
  return EQUIP_CHIP_COLORS[name] || EQUIP_CHIP_DEFAULT;
}

export default function ProcessingTab({ job, updateJobFields, editing }) {
  const [hov, setHov] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const captureLog = job.captureLog || [];
  const captured = captureLog.filter((r) => r.status === "captured");

  const upd = (field, val) => updateJobFields({ [field]: val });
  const updateLog = (id, field, val) => {
    upd("captureLog", captureLog.map((r) => r.id === id ? { ...r, [field]: val } : r));
  };

  const previewCount = captured.filter((r) => r.preview).length;
  const hrReqCount = captured.filter((r) => r.hiResRequested).length;
  const hrDoneCount = captured.filter((r) => r.hiResProcessed).length;
  const allPreview = captured.length > 0 && previewCount === captured.length;
  const allHrReq = captured.length > 0 && hrReqCount === captured.length;

  const toggleAll = (field) => {
    const allChecked = captured.every((r) => r[field]);
    const ids = new Set(captured.map((r) => r.id));
    upd("captureLog", captureLog.map((r) => ids.has(r.id) ? { ...r, [field]: !allChecked } : r));
  };

  if (captured.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 32, color: C.muted }}>
        <div style={{ fontSize: 13, marginBottom: 6 }}>No captured items yet</div>
        <div style={{ fontSize: 11 }}>Mark scans as captured in the Capture tab to begin processing.</div>
      </div>
    );
  }

  const gridCols = "30px 46px minmax(140px,1.2fr) minmax(130px,1fr) 0.7fr minmax(130px,1fr) 58px 58px 58px minmax(150px,1fr)";
  const headers = ["#", "Photo", "Subject", "Equipment", "Storage", "Capture Notes", "Preview", "HR Req", "HR Done", "Processing Notes"];
  const cellInp = { width: "100%", background: "transparent", border: "none", outline: "none", color: C.text, fontSize: 12, fontFamily: fl, fontWeight: 400, padding: 0 };
  const cellTxt = (v) => ({ cursor: editing ? "text" : "default", display: "block", minHeight: 16, fontSize: 12, color: v ? C.text : `${C.muted}55`, fontFamily: fl, fontWeight: 400 });
  const checkboxStyle = { accentColor: C.blue, cursor: editing ? "pointer" : "default", width: 14, height: 14 };
  const headerCheckStyle = { accentColor: C.blue, cursor: editing ? "pointer" : "default", width: 12, height: 12 };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Processing</label>
            <span style={{ fontSize: 10, color: C.muted }}>{captured.length} items</span>
          </div>
        </div>

        <div className="jc-cardtable" style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflowX: "auto" }}>
          <div className="jc-cardtable-head" style={{ display: "grid", gridTemplateColumns: gridCols, background: C.surfaceAlt, borderBottom: `1px solid ${C.border}`, minWidth: "fit-content" }}>
            {headers.map((h, i) => {
              if (h === "Preview" || h === "HR Req") {
                const field = h === "Preview" ? "preview" : "hiResRequested";
                const checked = h === "Preview" ? allPreview : allHrReq;
                return (
                  <div key={h} style={S.headerCenter}>
                    <span>{h}</span>
                    <input type="checkbox" checked={checked} onChange={editing ? () => toggleAll(field) : undefined} disabled={!editing} style={{ ...headerCheckStyle, opacity: editing ? 1 : 0.4 }} />
                  </div>
                );
              }
              return <div key={h} style={{ ...S.header, color: h === "Equipment" ? C.blue : C.muted, borderRight: i < headers.length - 1 ? `1px solid ${C.border}` : "none" }}>{h}</div>;
            })}
          </div>

          {captured.map((row, idx) => {
            const photo = row.images?.[0] || null;
            const equipNames = row.equipment ? Object.keys(row.equipment).filter((k) => row.equipment[k]) : [];
            const isEditNotes = editingCell?.rid === row.id && editingCell?.f === "processingNotes";
            return (
              <div className="jc-cardtable-row" key={row.id} onMouseEnter={() => setHov(row.id)} onMouseLeave={() => setHov(null)} style={{ display: "grid", gridTemplateColumns: gridCols, borderBottom: idx < captured.length - 1 ? `1px solid ${C.border}` : "none", background: hov === row.id ? C.surfaceAlt : "transparent", alignItems: "center", minWidth: "fit-content" }}>
                <div style={S.rowNum} data-label="#">{idx + 1}</div>
                <div style={S.photoCell} data-label="Photo">{photo?.src ? <img src={photo.src} alt="" style={S.thumb} /> : <div style={S.emptyThumb}>-</div>}</div>
                <div style={S.textCell} data-label="Subject">{subjectDisplay(row) || "-"}</div>
                <div style={S.equipCell} data-label="Equipment">
                  {equipNames.map((name) => {
                    const color = getEquipColor(name);
                    return <span key={name} style={{ ...S.equipChip, background: color.bg, border: `1px solid ${color.border}`, color: color.text }}>{name}</span>;
                  })}
                  {equipNames.length === 0 && <span style={S.emptyText}>-</span>}
                </div>
                <div style={S.textCell} data-label="Storage">{row.storage || "-"}</div>
                <div style={S.textCell} data-label="Capture Notes">{row.notes || "-"}</div>
                <CheckCell label="Preview" checked={!!row.preview} editing={editing} style={checkboxStyle} onChange={() => updateLog(row.id, "preview", !row.preview)} />
                <CheckCell label="HR Req" checked={!!row.hiResRequested} editing={editing} style={checkboxStyle} onChange={() => updateLog(row.id, "hiResRequested", !row.hiResRequested)} testId={`processing-hires-req-${row.id}`} />
                <CheckCell label="HR Done" checked={!!row.hiResProcessed} editing={editing} style={checkboxStyle} onChange={() => updateLog(row.id, "hiResProcessed", !row.hiResProcessed)} />
                <div style={{ padding: "9px 8px" }} data-label="Processing Notes">
                  {editing && isEditNotes ? (
                    <input autoFocus aria-label="Processing Notes" value={row.processingNotes || ""} onChange={(e) => updateLog(row.id, "processingNotes", e.target.value)} onBlur={() => setEditingCell(null)} onKeyDown={(e) => { if (e.key === "Enter") setEditingCell(null); }} style={cellInp} />
                  ) : (
                    <span onClick={editing ? () => setEditingCell({ rid: row.id, f: "processingNotes" }) : undefined} style={cellTxt(row.processingNotes)}>{row.processingNotes || "-"}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={S.summary}>
          <div style={{ display: "flex", gap: 12 }}>
            <span style={{ color: previewCount === captured.length ? C.blue : C.muted }}>{previewCount} previewed</span>
            <span style={{ color: hrReqCount > 0 ? C.amber : C.muted }}>{hrReqCount} hi-res requested</span>
            <span style={{ color: hrDoneCount > 0 ? C.green : C.muted }}>{hrDoneCount} hi-res processed</span>
          </div>
          <span>{captured.length} items</span>
        </div>
      </div>
    </div>
  );
}

function CheckCell({ checked, editing, style, onChange, testId, label }) {
  return (
    <div style={S.checkCell} data-label={label}>
      <input data-testid={testId} aria-label={label} type="checkbox" checked={checked} onChange={editing ? onChange : undefined} disabled={!editing} style={{ ...style, opacity: editing ? 1 : 0.6 }} />
    </div>
  );
}

const S = {
  header: { padding: "8px 6px", fontSize: 9, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: fl, whiteSpace: "nowrap", overflow: "hidden" },
  headerCenter: { padding: "8px 4px", fontSize: 9, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.08em", color: C.muted, fontFamily: fl, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 },
  rowNum: { padding: "9px 6px", fontSize: 11, color: `${C.muted}66`, borderRight: `1px solid ${C.border}`, fontFamily: "monospace" },
  photoCell: { padding: "4px", borderRight: `1px solid ${C.border}` },
  thumb: { width: 36, height: 36, borderRadius: 3, objectFit: "cover", display: "block", border: `1px solid ${C.border}` },
  emptyThumb: { width: 36, height: 36, borderRadius: 3, border: `1px solid ${C.border}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: `${C.muted}44` },
  textCell: { padding: "9px 8px", borderRight: `1px solid ${C.border}`, fontSize: 12, color: C.text, fontFamily: fl, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  equipCell: { display: "flex", flexWrap: "wrap", gap: 4, padding: "6px", minHeight: 40, alignItems: "center", borderRight: `1px solid ${C.border}` },
  equipChip: { display: "inline-flex", alignItems: "center", padding: "3px 8px", borderRadius: 3, fontSize: 10, whiteSpace: "nowrap", fontWeight: 500 },
  emptyText: { fontSize: 10, color: `${C.muted}55` },
  checkCell: { padding: "9px 4px", borderRight: `1px solid ${C.border}`, textAlign: "center" },
  summary: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, padding: "0 4px", fontSize: 10, color: C.muted, fontFamily: fl },
};
